/*
 * Settings → Operations → Pipeline & Facility (fieldiq-api#1196, PF-1).
 *
 * Two sections:
 *   - the company defaults (GET / PUT /pipeline-facility/settings): ticket
 *     mode, code, calibration warning days;
 *   - per customer (GET / PUT /pipeline-facility/customers/:id/settings): its
 *     own ticket mode and code (blank = the company default), and who signs.
 *
 * A tenant with no row reads the API's defaults with `configured: false`, and
 * the page says so. Codes and ticket modes come from GET /meta with the API's
 * labels. Only changed fields are sent; PUT keeps what it is not handed.
 *
 * Writes are PF_ADMIN (admin, office_staff); everyone else reads with the
 * controls disabled. A 403 is said as a refusal, never as a saved toast.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  usePfCustomers,
  usePfCustomerSettingsList,
  usePfMeta,
  usePfSettings,
  useUpdatePfCustomerSettings,
  useUpdatePfSettings,
} from '../../hooks/usePipelineFacility';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { blankToNull, canAdminPf, catalogLabel, pfErrorMessage } from '../../lib/pipeline-facility';
import type { PfCustomerSettings, PfCustomerSettingsUpdate, PfSettingsUpdate } from '../../types/pipeline-facility';

const inputCls =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';
const btnPrimary = 'flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed';
const btnSecondary =
  'inline-flex items-center gap-1.5 rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm font-semibold text-fieldiq-text hover:bg-fieldiq-dark/60 disabled:opacity-60';

function ErrorLine({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      {message}
    </p>
  );
}

export default function PipelineFacilitySettingsPage() {
  const { user } = useAuth();
  const canWrite = canAdminPf(user?.role);
  const query = usePfSettings();
  const catalog = usePfMeta().data?.data ?? null;
  const save = useUpdatePfSettings();
  const settings = query.data?.data ?? null;
  const failure = queryLoadFailure(query);

  const [ticketMode, setTicketMode] = useState('per_day');
  const [code, setCode] = useState('');
  const [calWarn, setCalWarn] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setTicketMode(settings.defaultTicketMode);
      setCode(settings.defaultCode ?? '');
      setCalWarn(settings.calWarnDays == null ? '' : String(settings.calWarnDays));
    }
  }, [settings]);

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" aria-label="Loading pipeline settings" />
      </div>
    );
  }
  if (failure || !settings) {
    return (
      <div className="px-6 py-4">
        <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="pipeline settings" />
      </div>
    );
  }

  const initialCal = settings.calWarnDays == null ? '' : String(settings.calWarnDays);
  const dirty = ticketMode !== settings.defaultTicketMode || code !== (settings.defaultCode ?? '') || calWarn.trim() !== initialCal;

  const onSave = () => {
    setError(null);
    const body: PfSettingsUpdate = {};
    if (ticketMode !== settings.defaultTicketMode) body.default_ticket_mode = ticketMode;
    if (code !== (settings.defaultCode ?? '')) body.default_code = code || null;
    if (calWarn.trim() !== initialCal) {
      const n = Number(calWarn.trim());
      if (!Number.isInteger(n)) return setError('Calibration warning days must be a whole number, e.g. 30.');
      body.cal_warn_days = n;
    }
    save.mutate(body, {
      onSuccess: () => toast.success('Pipeline settings saved'),
      onError: (err) => setError(pfErrorMessage(err, 'The settings could not be saved.')),
    });
  };

  return (
    <div className="min-h-full" data-testid="pf-settings-page">
      <div className="px-6 py-4 space-y-4">
        {canWrite ? (
          <div className="flex justify-end">
            <button type="button" onClick={onSave} disabled={!dirty || save.isPending} className={btnPrimary}>
              {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
              {save.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-fieldiq-muted" data-testid="pf-settings-readonly">
            Only an admin or office staff can change pipeline settings.
          </p>
        )}
        {!settings.configured && (
          <p className="text-xs text-fieldiq-muted" data-testid="pf-settings-defaults">
            These are the defaults. Nobody has saved pipeline settings for this company yet.
          </p>
        )}
        <ErrorLine message={error} />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label htmlFor="pf-ticket-mode" className="text-sm font-medium text-white block mb-1">Default ticket mode</label>
            <select id="pf-ticket-mode" className={inputCls} value={ticketMode} disabled={!canWrite} onChange={(e) => setTicketMode(e.target.value)}>
              {(catalog?.ticketModes ?? [{ value: ticketMode, label: ticketMode }]).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-fieldiq-muted mt-1">Used when a customer has no setting of its own.</p>
          </div>
          <div>
            <label htmlFor="pf-code" className="text-sm font-medium text-white block mb-1">Default code</label>
            <select id="pf-code" className={inputCls} value={code} disabled={!canWrite} onChange={(e) => setCode(e.target.value)}>
              <option value="">None</option>
              {(catalog?.codes ?? []).map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-fieldiq-muted mt-1">The design code new work is assessed against.</p>
          </div>
          <div>
            <label htmlFor="pf-cal-warn" className="text-sm font-medium text-white block mb-1">Calibration warning days</label>
            <input id="pf-cal-warn" className={inputCls} inputMode="numeric" value={calWarn} disabled={!canWrite} onChange={(e) => setCalWarn(e.target.value)} />
            <p className="text-xs text-fieldiq-muted mt-1">1 to 365. Instruments due within this many days show Due soon.</p>
          </div>
        </div>

        <CustomerSettingsSection canWrite={canWrite} />
      </div>
    </div>
  );
}

function CustomerSettingsSection({ canWrite }: { canWrite: boolean }) {
  const customers = usePfCustomers();
  const catalog = usePfMeta().data?.data ?? null;
  const list = customers.data ?? [];
  const settings = usePfCustomerSettingsList(list.map((c) => c.id));
  const [editing, setEditing] = useState<string | null>(null);
  const failure = queryLoadFailure(customers);

  return (
    <section className="space-y-3 border-t border-fieldiq-border pt-4" data-testid="pf-customer-settings">
      <div>
        <h2 className="text-base font-bold text-white">Per customer</h2>
        <p className="text-xs text-fieldiq-muted">A blank ticket mode or code uses the company default above.</p>
      </div>
      {customers.isLoading ? (
        <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" aria-label="Loading customers" />
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(customers)} kind={failure} subject="customers" />
      ) : list.length === 0 ? (
        <p className="text-sm text-fieldiq-text">There are no customers yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-fieldiq-border">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-4 py-3">Customer</th>
                <th scope="col" className="px-4 py-3">Ticket mode</th>
                <th scope="col" className="px-4 py-3">Code</th>
                <th scope="col" className="px-4 py-3">Signer</th>
                {canWrite && <th scope="col" className="px-4 py-3"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {list.map((c, i) => {
                const q = settings[i];
                const cs = q?.data?.data ?? null;
                const rowFailure = q ? queryLoadFailure(q) : null;
                return (
                  <React.Fragment key={c.id}>
                    <tr className="border-t border-fieldiq-border" data-testid="pf-customer-settings-row">
                      <td className="px-4 py-3 font-semibold text-white">{c.name}</td>
                      {q?.isLoading ? (
                        <td colSpan={3} className="px-4 py-3 text-fieldiq-muted">Loading…</td>
                      ) : rowFailure || !cs ? (
                        <td colSpan={3} className="px-4 py-3 text-fieldiq-text">
                          {rowFailure === 'refused' ? 'You do not have access to this customer’s settings.' : 'Could not load this customer’s settings.'}
                        </td>
                      ) : (
                        <>
                          <td className="px-4 py-3 text-fieldiq-text">
                            {cs.ticketMode ? catalogLabel(catalog?.ticketModes, cs.ticketMode) : `Default (${catalogLabel(catalog?.ticketModes, cs.effectiveTicketMode)})`}
                          </td>
                          <td className="px-4 py-3 text-fieldiq-text">
                            {cs.defaultCode
                              ? cs.defaultCodeLabel ?? catalogLabel(catalog?.codes, cs.defaultCode)
                              : cs.effectiveCode
                                ? `Default (${catalogLabel(catalog?.codes, cs.effectiveCode)})`
                                : '—'}
                          </td>
                          <td className="px-4 py-3 text-fieldiq-text">
                            {cs.signerName || '—'}
                            {cs.signerTitle && <span className="block text-xs text-fieldiq-muted">{cs.signerTitle}</span>}
                          </td>
                        </>
                      )}
                      {canWrite && (
                        <td className="px-4 py-3 text-right">
                          {cs && editing !== c.id && (
                            <button type="button" className={btnSecondary} onClick={() => setEditing(c.id)} aria-label={`Edit settings for ${c.name}`}>
                              <Pencil className="size-4" aria-hidden="true" />
                              Edit
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                    {canWrite && editing === c.id && cs && (
                      <tr className="border-t border-fieldiq-border bg-fieldiq-dark/30">
                        <td colSpan={5} className="px-4 py-4">
                          <CustomerSettingsForm settings={cs} onDone={() => setEditing(null)} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CustomerSettingsForm({ settings, onDone }: { settings: PfCustomerSettings; onDone: () => void }) {
  const catalog = usePfMeta().data?.data ?? null;
  const save = useUpdatePfCustomerSettings();
  const [ticketMode, setTicketMode] = useState(settings.ticketMode ?? '');
  const [code, setCode] = useState(settings.defaultCode ?? '');
  const [signerName, setSignerName] = useState(settings.signerName ?? '');
  const [signerTitle, setSignerTitle] = useState(settings.signerTitle ?? '');
  const [error, setError] = useState<string | null>(null);

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const body: PfCustomerSettingsUpdate = {};
    if (ticketMode !== (settings.ticketMode ?? '')) body.ticket_mode = ticketMode || null;
    if (code !== (settings.defaultCode ?? '')) body.default_code = code || null;
    if (signerName !== (settings.signerName ?? '')) body.signer_name = blankToNull(signerName);
    if (signerTitle !== (settings.signerTitle ?? '')) body.signer_title = blankToNull(signerTitle);
    if (Object.keys(body).length === 0) return setError('Nothing has changed.');
    save.mutate(
      { customerId: settings.customerId, body },
      {
        onSuccess: () => {
          toast.success(`Saved settings for ${settings.customerName ?? 'the customer'}`);
          onDone();
        },
        onError: (err) => setError(pfErrorMessage(err, 'The customer settings could not be saved.')),
      },
    );
  };

  return (
    <form onSubmit={onSave} className="space-y-3" data-testid="pf-customer-settings-form">
      <ErrorLine message={error} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="block text-sm text-white">
          Ticket mode
          <select className={`${inputCls} mt-1`} value={ticketMode} onChange={(e) => setTicketMode(e.target.value)}>
            <option value="">Company default</option>
            {(catalog?.ticketModes ?? []).map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm text-white">
          Code
          <select className={`${inputCls} mt-1`} value={code} onChange={(e) => setCode(e.target.value)}>
            <option value="">Company default</option>
            {(catalog?.codes ?? []).map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm text-white">
          Signer name
          <input className={`${inputCls} mt-1`} value={signerName} onChange={(e) => setSignerName(e.target.value)} />
        </label>
        <label className="block text-sm text-white">
          Signer title
          <input className={`${inputCls} mt-1`} value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} />
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" className={btnSecondary} onClick={onDone} disabled={save.isPending}>
          Cancel
        </button>
        <button type="submit" className={btnPrimary} disabled={save.isPending}>
          {save.isPending ? 'Saving...' : 'Save customer'}
        </button>
      </div>
    </form>
  );
}
